import { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('scrawl_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch user data from token
  const fetchUser = async (token) => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        // Token invalid, remove it
        localStorage.removeItem('scrawl_token');
        
        // Show session expiration notification if user was previously logged in
        if (res.status === 401) {
          toast.error('Session expired. Please log in again! 🔐', {
            duration: 4000,
            position: 'top-right',
            style: {
              background: '#0f3460',
              color: '#eaeaea',
              border: '3px solid #ffc947',
              fontFamily: 'JetBrains Mono, monospace',
              fontWeight: '600'
            }
          });
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('scrawl_token');
    } finally {
      setLoading(false);
    }
  };

  // Register
  const register = async (username, email, password) => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      localStorage.setItem('scrawl_token', data.token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Login
  const login = async (identifier, password) => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('scrawl_token', data.token);
      setUser(data.user);
      
      // Show success notification
      toast.success(`Welcome back, ${data.user.username}! 🎉`, {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #e94560',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      
      // Show error notification for failed login
      toast.error(`Login failed: ${err.message} 🚨`, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#0f3460',
          color: '#eaeaea',
          border: '3px solid #ff6b6b',
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: '600'
        }
      });
      
      return { success: false, error: err.message };
    }
  };

  // Logout
  const logout = async () => {
    const currentUser = user;
    
    try {
      await logoutUser(); // Clear server-side cookie
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('scrawl_token');
    setUser(null);
    
    // Show logout notification
    toast.success(`Goodbye${currentUser?.username ? `, ${currentUser.username}` : ''}! See you soon 👋`, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: '#0f3460',
        color: '#eaeaea',
        border: '3px solid #ffc947',
        fontFamily: 'JetBrains Mono, monospace',
        fontWeight: '600'
      }
    });
  };

  // Set auth data directly (for OTP verification)
  const setAuthData = (token, userData) => {
    localStorage.setItem('scrawl_token', token);
    setUser(userData);
    
    // Show welcome notification for new registrations
    toast.success(`Account verified! Welcome to Scrawl, ${userData.username}! 🚀`, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#0f3460',
        color: '#eaeaea',
        border: '3px solid #0eff9f',
        fontFamily: 'JetBrains Mono, monospace',
        fontWeight: '600'
      }
    });
  };

  // Update profile
  const updateProfile = async (updates) => {
    const token = localStorage.getItem('scrawl_token');
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify(updates),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Update failed');
      }

      setUser(data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Get auth token for API calls
  const getToken = () => localStorage.getItem('scrawl_token');

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    setAuthData,
    updateProfile,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
